import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export const OPEN_STAGE_EVENT = "excellent:open-stage";

export type StageOccupantKind = "play" | "chat";

export interface OpenStageDetail {
  kind: StageOccupantKind;
  slug: string;
  name?: string;
  startActionKey?: string;
}

export interface AppTab {
  kind: StageOccupantKind;
  slug: string;
  name: string;
  startActionKey?: string;
}

interface StageContextValue {
  open: boolean;
  tabs: AppTab[];
  activeSlug: string | null;
  openStage: (slug?: string) => void;
  minimize: () => void;
  openStageOccupant: (input: OpenStageDetail) => void;
  closeTab: (slug: string) => void;
  setActive: (slug: string) => void;
}

const StageContext = createContext<StageContextValue | null>(null);
const STORAGE_KEY = "stage:tabs-v1";

interface PersistedState {
  tabs: AppTab[];
  activeSlug: string | null;
}

function isStageKind(value: unknown): value is StageOccupantKind {
  return value === "play" || value === "chat";
}

function readPersisted(): PersistedState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedState;
    if (!parsed || !Array.isArray(parsed.tabs)) return null;
    const tabs = parsed.tabs.filter((tab) => isStageKind(tab.kind));
    return {
      tabs,
      activeSlug: tabs.some((tab) => tab.slug === parsed.activeSlug)
        ? parsed.activeSlug
        : (tabs[0]?.slug ?? null),
    };
  } catch {
    return null;
  }
}

function writePersisted(state: PersistedState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function StageProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [tabs, setTabs] = useState<AppTab[]>([]);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);

  useEffect(() => {
    const saved = readPersisted();
    if (!saved || saved.tabs.length === 0) return;
    setTabs(saved.tabs);
    setActiveSlug(saved.activeSlug);
  }, []);

  useEffect(() => {
    writePersisted({ tabs, activeSlug });
  }, [tabs, activeSlug]);

  const openStage = useCallback((slug?: string) => {
    setOpen(true);
    if (slug) setActiveSlug(slug);
  }, []);

  const minimize = useCallback(() => setOpen(false), []);

  const openStageOccupant = useCallback((input: OpenStageDetail) => {
    if (!input.slug || !isStageKind(input.kind)) return;
    setTabs((prev) => {
      const existing = prev.find((tab) => tab.slug === input.slug);
      if (existing) {
        return prev.map((tab) =>
          tab.slug === input.slug
            ? {
                ...tab,
                name: input.name ?? tab.name,
                startActionKey: input.startActionKey,
              }
            : tab,
        );
      }
      return [
        ...prev,
        {
          kind: input.kind,
          slug: input.slug,
          name: input.name ?? input.slug,
          startActionKey: input.startActionKey,
        },
      ];
    });
    setActiveSlug(input.slug);
    setOpen(true);
  }, []);

  const closeTab = useCallback((slug: string) => {
    setTabs((prev) => {
      const idx = prev.findIndex((tab) => tab.slug === slug);
      if (idx < 0) return prev;
      const next = prev.filter((tab) => tab.slug !== slug);
      setActiveSlug((cur) => {
        if (cur !== slug) return cur;
        return next[Math.max(0, idx - 1)]?.slug ?? next[0]?.slug ?? null;
      });
      if (next.length === 0) setOpen(false);
      return next;
    });
  }, []);

  const setActive = useCallback((slug: string) => {
    setActiveSlug(slug);
  }, []);

  useEffect(() => {
    function onOpenStage(e: Event) {
      const detail = (e as CustomEvent<OpenStageDetail>).detail;
      openStageOccupant(detail);
    }
    window.addEventListener(OPEN_STAGE_EVENT, onOpenStage as EventListener);
    return () =>
      window.removeEventListener(OPEN_STAGE_EVENT, onOpenStage as EventListener);
  }, [openStageOccupant]);

  const value = useMemo<StageContextValue>(
    () => ({
      open,
      tabs,
      activeSlug,
      openStage,
      minimize,
      openStageOccupant,
      closeTab,
      setActive,
    }),
    [open, tabs, activeSlug, openStage, minimize, openStageOccupant, closeTab, setActive],
  );

  return <StageContext.Provider value={value}>{children}</StageContext.Provider>;
}

export function useStage(): StageContextValue {
  const ctx = useContext(StageContext);
  if (!ctx) throw new Error("useStage must be used within StageProvider");
  return ctx;
}
